import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, ChevronLeft, Globe, Navigation, Clock, ArrowLeft, Users, AlertCircle, Map } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useReportFilters } from './useReportFilters';
import { DateNavInput } from './DateNavInput';
import { toast } from 'sonner';
import { formatUtcTime } from '@/utils/dateUtils';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createIcon = (color) => new L.DivIcon({
  className: '',
  html: `<div style="width:18px;height:18px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const startIcon = createIcon('#22c55e');
const endIcon = createIcon('#ef4444');
const withinIcon = createIcon('#3b82f6');
const outsideIcon = createIcon('#f59e0b');

// Auto-fit map to all markers
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions && positions.length > 0) {
      if (positions.length === 1) {
        map.setView(positions[0], 14);
      } else {
        map.fitBounds(L.latLngBounds(positions), { padding: [40, 40] });
      }
    }
  }, [positions, map]);
  return null;
}

const today = () => new Date(Date.now()).toISOString().split('T')[0];

export default function GPSTrackingReportPage() {
  const navigate = useNavigate();
  const { timezone, todayDate } = useReportFilters();

  const [selectedDate, setSelectedDate] = useState('');

  // Set correct date once company timezone is known
  useEffect(() => {
    if (todayDate) setSelectedDate(todayDate);
  }, [todayDate]);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Detail view state
  const [detailStaff, setDetailStaff] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // ----- Summary -----
  useEffect(() => {
    fetchSummary();
  }, [selectedDate]);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    setSummary(null);
    setDetailStaff(null);
    try {
      const res = await axios.get(`${API}/admin/reports/gps-summary`, {
        params: { date: selectedDate }, withCredentials: true
      });
      setSummary(res.data);
    } catch { toast.error('Failed to load GPS summary'); }
    finally { setLoadingSummary(false); }
  };

  // ----- Detail -----
  const openDetail = (staff) => {
    setDetailStaff(staff);
  };

  useEffect(() => {
    if (detailStaff) fetchDetail();
  }, [detailStaff]);

  const fetchDetail = async () => {
    setLoadingDetail(true);
    setDetailData(null);
    try {
      const res = await axios.get(
        `${API}/admin/staff/${detailStaff.staff_id}/gps-history`,
        { params: { date: selectedDate }, withCredentials: true }
      );
      setDetailData(res.data);
    } catch { toast.error('Failed to load GPS data'); }
    finally { setLoadingDetail(false); }
  };

    const formatTime = (iso) => formatUtcTime(iso);


  // ======= DETAIL VIEW =======
  if (detailStaff) {
    const events = detailData?.locations || detailData?.history || [];
    // Use actual fetched counts when available
    const totalCount = detailData ? events.length : detailStaff.total;
    const activeCount = detailData
      ? events.filter(e => e.within_radius === true || e.event_type === 'check_in').length
      : detailStaff.active;
    const idleCount = detailData
      ? events.filter(e => e.within_radius === false || e.event_type === 'check_out').length
      : detailStaff.idle;

    return (
      <div className="space-y-5" data-testid="gps-tracking-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setDetailStaff(null)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-gray-600"
          >
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">{detailStaff.name} — GPS History</h1>
            <p className="text-sm text-gray-500">{selectedDate} · <span className="text-gray-400">{timezone}</span></p>
          </div>
          {events.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMap(true)}
              className="flex items-center gap-1.5"
              data-testid="open-map-btn"
            >
              <Map className="w-4 h-4 text-indigo-600" />
              Open Map
            </Button>
          )}
        </div>

        {loadingDetail && <div className="text-center py-12 text-gray-500">Loading GPS data...</div>}

        {!loadingDetail && (
          <>
            {/* Staff quick stats */}
            <div className="flex gap-3 flex-wrap">
              <div className="px-4 py-2 bg-blue-50 rounded-xl flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-blue-700">{totalCount} Total Events</span>
              </div>
              <div className="px-4 py-2 bg-indigo-50 rounded-xl flex items-center gap-2">
                <Navigation className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-semibold text-indigo-700">{activeCount} Active</span>
              </div>
              <div className={`px-4 py-2 rounded-xl flex items-center gap-2 ${
                idleCount > activeCount ? 'bg-red-50' : 'bg-amber-50'
              }`}>
                <AlertCircle className={`w-4 h-4 ${idleCount > activeCount ? 'text-red-500' : 'text-amber-500'}`} />
                <span className={`text-sm font-semibold ${idleCount > activeCount ? 'text-red-700' : 'text-amber-700'}`}>
                  {idleCount} Idle
                </span>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Location Events</CardTitle>
              </CardHeader>
              <CardContent>
                {events.length > 0 ? (
                  <div className="space-y-3">
                    {events.map((ev, i) => {
                      // within_radius: null/undefined = no GPS config → neutral
                      // true = within area, false = outside area
                      const hasGpsArea = ev.within_radius === true || ev.within_radius === false;
                      const isWithinArea = !hasGpsArea || ev.within_radius === true;
                      return (
                        <div key={i} className="flex items-start gap-4 p-4 rounded-xl border bg-gray-50 hover:bg-blue-50 transition-colors">
                          <div className={`mt-0.5 p-2 rounded-full flex-shrink-0 ${!hasGpsArea ? 'bg-blue-100' : isWithinArea ? 'bg-indigo-100' : 'bg-red-100'}`}>
                            {!hasGpsArea
                              ? <MapPin className="w-4 h-4 text-blue-600" />
                              : isWithinArea
                                ? <Navigation className="w-4 h-4 text-indigo-600" />
                                : <MapPin className="w-4 h-4 text-red-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {hasGpsArea ? (
                                <Badge className={isWithinArea ? 'bg-indigo-100 text-indigo-700' : 'bg-red-100 text-red-700'}>
                                  {isWithinArea ? 'Within Area' : 'Outside Area'}
                                </Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-700">Location</Badge>
                              )}
                              <span className="text-sm font-semibold text-gray-700">
                                {formatTime(ev.timestamp || ev.created_at)}
                              </span>
                            </div>
                            {(ev.latitude || ev.check_in_lat) && (
                              <p className="text-xs text-gray-500 mt-1 font-mono">
                                {(ev.latitude || ev.check_in_lat)?.toFixed(6)}, {(ev.longitude || ev.check_in_lng)?.toFixed(6)}
                              </p>
                            )}
                            {ev.address && <p className="text-sm text-gray-600 mt-1 truncate">{ev.address}</p>}
                          </div>
                          <div className="flex-shrink-0">
                            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1 justify-end">
                              <Clock className="w-3 h-3" />
                              {formatTime(ev.timestamp || ev.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <MapPin className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                    <p className="text-gray-500">No GPS events on this date</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Map Modal */}
        <Dialog open={showMap} onOpenChange={setShowMap}>
          <DialogContent className="max-w-4xl" style={{ height: '80vh' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Map className="w-5 h-5 text-indigo-600" />
                GPS Route — {detailStaff.name} · {selectedDate}
              </DialogTitle>
            </DialogHeader>
            <div style={{ height: 'calc(80vh - 100px)', position: 'relative' }}>
              {events.filter(e => e.latitude).length > 0 ? (() => {
                const validEvents = [...events].filter(e => e.latitude && e.longitude).reverse(); // oldest first
                const positions = validEvents.map(e => [e.latitude, e.longitude]);
                return (
                  <MapContainer
                    center={positions[0] || [0, 0]}
                    zoom={13}
                    style={{ width: '100%', height: '100%', borderRadius: '8px' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Route polyline */}
                    {positions.length > 1 && (
                      <Polyline
                        positions={positions}
                        pathOptions={{ color: '#6366f1', weight: 3, opacity: 0.8 }}
                      />
                    )}

                    {/* Start marker (green) */}
                    <Marker position={positions[0]} icon={startIcon}>
                      <Popup>
                        <strong className="text-indigo-600">Start</strong><br />
                        {formatTime(validEvents[0].timestamp || validEvents[0].created_at)}
                      </Popup>
                    </Marker>

                    {/* End marker (red) */}
                    {positions.length > 1 && (
                      <Marker position={positions[positions.length - 1]} icon={endIcon}>
                        <Popup>
                          <strong className="text-red-600">End</strong><br />
                          {formatTime(validEvents[validEvents.length - 1].timestamp || validEvents[validEvents.length - 1].created_at)}
                        </Popup>
                      </Marker>
                    )}

                    {/* Intermediate markers */}
                    {validEvents.slice(1, -1).map((ev, i) => {
                      const within = ev.within_radius === true || ev.event_type === 'check_in';
                      return (
                        <Marker key={i} position={[ev.latitude, ev.longitude]} icon={within ? withinIcon : outsideIcon}>
                          <Popup>
                            <strong>{within ? 'Within Area' : 'Outside Area'}</strong><br />
                            {formatTime(ev.timestamp || ev.created_at)}<br />
                            <span className="font-mono text-xs">{ev.latitude?.toFixed(5)}, {ev.longitude?.toFixed(5)}</span>
                          </Popup>
                        </Marker>
                      );
                    })}

                    <FitBounds positions={positions} />
                  </MapContainer>
                );
              })() : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <MapPin className="w-12 h-12" />
                  <p className="ml-3">No coordinates available</p>
                </div>
              )}

              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000] text-xs flex gap-4">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-indigo-500"></div> Start</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500"></div> End</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-500"></div> Within Area</div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-amber-500"></div> Outside Area</div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ======= SUMMARY TABLE VIEW =======
  const staff = summary?.staff || [];
  const totalEvents = staff.reduce((a, s) => a + s.total, 0);

  return (
    <div className="space-y-5" data-testid="gps-tracking-report-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">GPS Tracking Report</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      {/* Date filter */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center gap-3">
            <Label className="text-xs text-gray-500">Date</Label>
            <DateNavInput value={selectedDate} onChange={setSelectedDate} />
          </div>
        </CardContent>
      </Card>

      {loadingSummary && <div className="text-center py-12 text-gray-500">Loading...</div>}

      {!loadingSummary && summary && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-blue-50 rounded-xl flex gap-3 items-center">
              <MapPin className="w-7 h-7 text-blue-500" />
              <div><p className="text-2xl font-bold text-blue-600">{totalEvents}</p><p className="text-xs text-gray-500">Total GPS Events</p></div>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-indigo-50 rounded-xl flex gap-3 items-center">
              <Users className="w-7 h-7 text-indigo-500" />
              <div><p className="text-2xl font-bold text-indigo-600">{staff.length}</p><p className="text-xs text-gray-500">Staff tracked</p></div>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-amber-50 rounded-xl flex gap-3 items-center">
              <AlertCircle className="w-7 h-7 text-amber-500" />
              <div><p className="text-2xl font-bold text-amber-600">{staff.filter(s=>s.idle>s.active).length}</p><p className="text-xs text-gray-500">Outside area more</p></div>
            </CardContent></Card>
          </div>

          {/* Staff table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Staff GPS Summary — {selectedDate}</CardTitle>
              <p className="text-xs text-gray-400 mt-0.5">Click a row to view GPS details</p>
            </CardHeader>
            <CardContent className="p-0">
              {staff.length === 0 ? (
                <div className="py-16 text-center">
                  <MapPin className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                  <p className="text-gray-500">No GPS events recorded on this date</p>
                  <p className="text-xs text-gray-400 mt-1">GPS data is recorded when staff check in/out with location enabled</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {['Staff Name', 'Department', 'Total Count', 'Active', 'Idle', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {staff.map((s) => {
                      const highIdle = s.idle > s.active;
                      return (
                        <tr
                          key={s.staff_id}
                          onClick={() => openDetail(s)}
                          className="hover:bg-indigo-50 cursor-pointer transition-colors"
                          data-testid={`gps-staff-row-${s.staff_id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs flex-shrink-0">
                                {s.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <span className="font-medium text-gray-800">{s.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">{s.department || '–'}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-blue-600">{s.total}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-indigo-600">{s.active}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${highIdle ? 'text-red-600' : 'text-amber-600'}`}>
                              {s.idle}
                              {highIdle && <span className="ml-1 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">High</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-indigo-500 hover:underline">View →</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
